import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="active-storage-upload"
export default class extends Controller {
  static targets = ["input", "progress", "progressText", "error", "success", "dropzone", "dropping", "fileList", "fileRowTemplate", "processing", "maxFilesError"]
  static values = {
    url: String,
    acceptedTypes: { type: String, default: "application/pdf" },
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 0 },
    detectCategory: { type: Boolean, default: false }
  }

  // Filename keywords -> job file category (matches JobFile::CATEGORY_ABBREVIATIONS)
  static FILE_CATEGORY_PATTERNS = [
    [["ntb", "notice", "notice_to_bid", "notice-to-bid", "noticetobid", "notice to bid"], "notice_to_bid"],
    [["addendum", "addenda"], "addendum"],
    [["tabulation", "bid_tab"], "bid_tabulations"],
    [["award", "bid_award"], "bid_awards"],
    [["qualification", "bid_qual"], "bid_qualifications"],
    [["budget", "bid info", "bidinfo", "bid_information"], "budget"],
    [["clarification"], "clarifications"],
    [["memo"], "memo"],
    [["drawing", "drawings", "plan", "plans", "dwg", "sheet", "sheets", "aia"], "drawings"],
    [["spec", "specs", "specification", "specifications"], "specifications"]
  ]

  connect() {
    this.uploadedFiles = []
    this.nextFileIndex = 0
    this.setupDragAndDrop()
    this.setupFileInput()
  }

  setupDragAndDrop() {
    if (!this.hasDropzoneTarget) return

    const dropzone = this.dropzoneTarget

    // Prevent default drag behaviors
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })


    this.boundHighlight = this.highlight.bind(this)
    this.boundUnhighlight = this.unhighlight.bind(this)

    // Highlight drop zone when item is dragged over it
    ;['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundHighlight, false)
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundUnhighlight, false)
    })

    // Handle dropped files
    dropzone.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  setupFileInput() {
    if (!this.hasInputTarget) return

    this.inputTarget.addEventListener('change', this.handleFileSelect.bind(this))
  }

  openFilePicker() {
    if (this.hasInputTarget) this.inputTarget.click()
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('hidden')
    }
    if (this.hasDroppingTarget && this.droppingTarget.classList.contains('hidden')) {
      this.droppingTarget.classList.remove('hidden')
    }
  }

  unhighlight(e) {
    // Don't unhighlight if we're moving to a child element
    if (e.relatedTarget && this.element.contains(e.relatedTarget)) {
      return
    }

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('hidden')
    }
    if (this.hasDroppingTarget) {
      this.droppingTarget.classList.add('hidden')
    }
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    const files = dt.files
    this.handleFiles(files)
    
    // Hide dropping target after drop
    if (this.hasDroppingTarget) {
      this.progressTarget.classList.remove('hidden')
      this.droppingTarget.classList.add('hidden')
    }
  }

  handleFileSelect(e) {
    const files = e.target.files
    this.handleFiles(files)
  }

  handleFiles(files) {
    if (files.length === 0) return
    this.hideMaxFilesError()

    // Validate all files first
    const validFiles = []
    for (const file of files) {
      if (!this.isValidFileType(file)) {
        this.showError("This file type isn't supported. Please upload a PDF.")
        return
      }
      validFiles.push(file)
    }

    // Enforce max file cap
    if (this.maxFilesValue > 0 && this.multipleValue) {
      const remaining = this.maxFilesValue - this.uploadedFiles.length
      if (remaining <= 0) {
        this.showMaxFilesError()
        return
      }
      if (validFiles.length > remaining) {
        validFiles.splice(remaining)
        this.showMaxFilesError()
      }
    }

    if (this.multipleValue) {
      this.uploadMultipleFiles(validFiles)
    } else {
      this.uploadFile(validFiles[0])
    }

    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
  }

  uploadMultipleFiles(files) {
    this.uploadQueue = files
    this.currentUploadIndex = 0

    this.showProgress()
    this.updateMultipleProgress()
    this.dispatch('uploading')
    this.uploadNextFile()
  }

  uploadNextFile() {
    if (this.currentUploadIndex >= this.uploadQueue.length) {
      // All uploads in this batch complete
      this.showDropzone()
      this.dispatch('uploaded')
      return
    }

    const file = this.uploadQueue[this.currentUploadIndex]
    const fileIndex = this.nextFileIndex
    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", event => {
          const fileProgress = event.loaded / event.total * 100
          this.updateMultipleProgress(fileProgress)
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        this.showError(`Upload failed for ${file.name}. Please try again.`)
        console.error('Upload error:', error)
        return
      }

      this.nextFileIndex += 1
      this.uploadedFiles.push({ blob, file, index: fileIndex })
      this.createHiddenInput(blob, fileIndex, file)
      this.renderFileList()
      this.dispatchFilesChanged()
      this.currentUploadIndex++
      this.uploadNextFile()
    })
  }

  updateMultipleProgress(fileProgress = 0) {
    if (this.hasProgressTextTarget) {
      const current = this.currentUploadIndex + 1
      const total = this.uploadQueue.length
      this.progressTextTarget.textContent = `Uploading ${current} of ${total}...`
    }
  }

  createHiddenInput(blob, index, file) {
    const form = this.element.closest('form')
    if (!form) return

    const fileInput = document.createElement('input')
    fileInput.type = 'hidden'
    fileInput.name = `job[job_files_attributes][${index}][file]`
    fileInput.value = blob.signed_id
    form.appendChild(fileInput)

    if (this.detectCategoryValue && file) {
      const category = this.detectCategoryFromFilename(file.name)
      const categoryInput = document.createElement('input')
      categoryInput.type = 'hidden'
      categoryInput.name = `job[job_files_attributes][${index}][category]`
      categoryInput.value = category
      form.appendChild(categoryInput)
    }
  }

  detectCategoryFromFilename(filename) {
    const normalized = filename.replace(/\s/g, '_').toLowerCase()
    for (const [keywords, category] of this.constructor.FILE_CATEGORY_PATTERNS) {
      if (keywords.some((kw) => normalized.includes(kw))) {
        return category
      }
    }
    return 'miscellaneous'
  }

  renderFileList() {
    if (!this.hasFileListTarget) return

    // Clear existing rows safely
    while (this.fileListTarget.firstChild) {
      this.fileListTarget.removeChild(this.fileListTarget.firstChild)
    }

    this.uploadedFiles.forEach((upload) => {
      const row = this.buildFileRow(upload)
      this.fileListTarget.appendChild(row)
    })
  }

  buildFileRow(upload) {
    if (this.hasFileRowTemplateTarget) {
      const row = this.fileRowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.fileIndex = upload.index
      row.querySelector('[data-role="filename"]').textContent = upload.file.name
      row.querySelector('[data-role="filesize"]').textContent = this.formatFileSize(upload.file.size)
      row.querySelector('[data-role="remove"]').addEventListener('click', () => this.removeFile(upload.index))
      return row
    }

    // Fallback for contexts without template
    const row = document.createElement('div')
    row.className = 'flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md text-sm'
    row.dataset.fileIndex = upload.index
    row.textContent = `${upload.file.name} (${this.formatFileSize(upload.file.size)})`
    return row
  }

  formatFileSize(bytes) {
    return bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  removeFile(fileIndex) {
    // Remove from tracked files
    this.uploadedFiles = this.uploadedFiles.filter(f => f.index !== fileIndex)

    const form = this.element.closest('form')
    if (form) {
      const fileInput = form.querySelector(`input[name="job[job_files_attributes][${fileIndex}][file]"]`)
      if (fileInput) fileInput.remove()
      const categoryInput = form.querySelector(`input[name="job[job_files_attributes][${fileIndex}][category]"]`)
      if (categoryInput) categoryInput.remove()
    }

    // Re-render and notify
    this.renderFileList()
    this.dispatchFilesChanged()

    // Hide max files error if now under cap
    if (this.maxFilesValue > 0 && this.uploadedFiles.length < this.maxFilesValue) {
      this.hideMaxFilesError()
    }
  }

  resetState() {
    this.uploadedFiles = []
    this.uploadQueue = []
    this.currentUploadIndex = 0
    this.nextFileIndex = 0

    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }

    const form = this.element.closest('form')
    if (form) {
      form
        .querySelectorAll('input[name^="job[job_files_attributes]"][name$="[file]"]')
        .forEach((input) => {
          if (input.type === 'hidden') input.remove()
        })
      form
        .querySelectorAll('input[name^="job[job_files_attributes]"][name$="[category]"]')
        .forEach((input) => {
          if (input.type === 'hidden') input.remove()
        })
    }

    if (this.hasFileListTarget) {
      while (this.fileListTarget.firstChild) {
        this.fileListTarget.removeChild(this.fileListTarget.firstChild)
      }
    }

    this.hideMaxFilesError()
    this.showDropzone()
    this.dispatchFilesChanged()
  }

  dispatchFilesChanged() {
    this.dispatch('filesChanged', {
      detail: {
        count: this.uploadedFiles.length,
        files: this.uploadedFiles.map(u => ({ name: u.file.name, size: u.file.size }))
      }
    })
  }

  showDropzone() {
    if (this.hasProgressTarget) this.progressTarget.classList.add('hidden')
    if (this.hasProcessingTarget) this.processingTarget.classList.add('hidden')
    if (this.hasSuccessTarget) this.successTarget.classList.add('hidden')
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden')
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove('hidden')
  }

  showMaxFilesError() {
    if (this.hasMaxFilesErrorTarget) {
      this.maxFilesErrorTarget.textContent = `Maximum ${this.maxFilesValue} files per upload`
      this.maxFilesErrorTarget.classList.remove('hidden')
    }
  }

  hideMaxFilesError() {
    if (this.hasMaxFilesErrorTarget) {
      this.maxFilesErrorTarget.classList.add('hidden')
    }
  }

  isValidFileType(file) {
    const acceptedTypes = this.acceptedTypesValue.split(',').map(type => type.trim())
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      } else {
        return file.type.match(new RegExp(type.replace('*', '.*')))
      }
    })
  }

  uploadFile(file) {
    this.showProgress()

    const upload = new DirectUpload(file, this.urlValue, this)

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        console.error('Upload error:', error)
      } else {
        this.showSuccess()
        this.createJobFileRecord(blob, file)
      }
    })
  }

  createJobFileRecord(blob, file) {
    // Show processing state while form submits
    this.showProcessing()

    // Create a hidden input with the blob signed ID
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'job[job_files_attributes][0][file]'
    input.value = blob.signed_id

    // Add to form
    const form = this.element.closest('form')
    if (form) {
      form.appendChild(input)

      // Trigger form submission
      const event = new CustomEvent('upload:done', {
        bubbles: true,
        detail: {
          blob: blob,
          file: file
        }
      })
      document.dispatchEvent(event)
    }
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = "Just a moment while the BiddyAI™ does its thing"
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }

    if (this.hasSuccessTarget) {
      this.successTarget.classList.add('hidden')
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
  }

  showSuccess() {
    if (this.hasSuccessTarget) {
      this.successTarget.classList.remove('hidden')
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
  }

  showProcessing() {
    if (this.hasProcessingTarget) {
      this.processingTarget.classList.remove('hidden')
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }

    if (this.hasSuccessTarget) {
      this.successTarget.classList.add('hidden')
    }
  }

  // DirectUpload delegate methods
  directUploadWillCreateBlobWithXHR(xhr) {
    xhr.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total * 100
      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = `Uploading ${Math.round(progress)}%`
      }
    })
  }

  directUploadWillStoreFileWithXHR(xhr) {
    // Optional: Add any additional headers or configuration
  }
} 
